import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { BookOpen, Target, Calendar, FileText, TrendingUp, Plus, Users, Clock, ArrowRight, Image as ImageIcon, Paperclip, UserCircle, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';

const Dashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [children, setChildren] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [goals, setGoals] = useState([]);
  const [completedWork, setCompletedWork] = useState([]);
  const [lessonPlans, setLessonPlans] = useState([]);
  const [pendingWork, setPendingWork] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
  const [selectedSemester, setSelectedSemester] = useState('all');
  const [userSemesters, setUserSemesters] = useState([]);

  useEffect(() => {
    loadData();
    checkSubscriptionStatus();
  }, []);

  const checkSubscriptionStatus = async () => {
    try {
      const userData = await api.getCurrentUser();
      // Redirect to subscription if payment is pending
      if (userData.subscription_status === 'pending_payment') {
        navigate('/subscription?signup=true');
      }
    } catch (error) {
      console.error('Failed to check subscription status:', error);
    }
  };

  const loadData = async () => {
    try {
      // PHASE 1: Load only critical data first (FAST)
      const [childrenData, subjectsData, userData] = await Promise.all([
        api.getChildren(),
        api.getSubjects(),
        api.getCurrentUser()
      ]);
      
      console.log('Dashboard loaded children:', childrenData);
      console.log('Children count:', childrenData?.length);
      
      setChildren(childrenData);
      // Sort subjects alphabetically by name
      const sortedSubjects = subjectsData.sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      setSubjects(sortedSubjects);
      
      // Load user's semester definitions
      let initialSemester = 'all';
      if (userData.semesters && userData.semesters.length > 0) {
        setUserSemesters(userData.semesters);
        
        // Set default to current semester
        const today = new Date();
        const currentSemester = userData.semesters.find(sem => {
          const startDate = new Date(sem.start_date || sem.startDate);
          const endDate = new Date(sem.end_date || sem.endDate);
          return today >= startDate && today <= endDate;
        });
        
        if (currentSemester) {
          initialSemester = currentSemester.name;
          setSelectedSemester(currentSemester.name);
        } else if (userData.semesters.length > 0) {
          // Default to first semester if no current semester found
          initialSemester = userData.semesters[0].name;
          setSelectedSemester(userData.semesters[0].name);
        }
      }
      
      if (childrenData.length > 0) {
        setSelectedChild(childrenData[0]);
      }
      
      // Show the dashboard immediately with children
      setLoading(false);
      
      // PHASE 2: Load secondary data in background (doesn't block UI)
      setTimeout(async () => {
        try {
          const [goalsData, pendingData] = await Promise.all([
            api.getGoals(),
            api.getPendingAssignments()
          ]);
          setGoals(goalsData);
          setPendingWork(pendingData);
        } catch (error) {
          console.error('Failed to load secondary data:', error);
        }
      }, 100);
      
      // PHASE 3: Load heavy data last (lazy load)
      setTimeout(async () => {
        try {
          const [plansData] = await Promise.all([
            api.getLessonPlans()
          ]);
          // Load completed work based on the initial semester that was set
          await loadCompletedWorkForSemester(initialSemester);
          setLessonPlans(plansData);
        } catch (error) {
          console.error('Failed to load completed work:', error);
        }
      }, 500);
      
    } catch (error) {
      console.error('Failed to load data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard data',
        variant: 'destructive'
      });
      setLoading(false);
    }
  };

  const loadCompletedWorkForSemester = async (semesterName) => {
    try {
      // Fetch ALL assignments (not just completed ones)
      const allAssignments = await api.getAssignments();
      
      // Filter by semester if needed
      let filteredAssignments = allAssignments;
      if (semesterName !== 'all') {
        const semester = userSemesters.find(s => s.name === semesterName);
        if (semester) {
          const startDate = new Date(semester.start_date || semester.startDate);
          const endDate = new Date(semester.end_date || semester.endDate);
          
          filteredAssignments = allAssignments.filter(a => {
            const dueDate = new Date(a.due_date);
            return dueDate >= startDate && dueDate <= endDate;
          });
        }
      }
      
      setCompletedWork(filteredAssignments);
    } catch (error) {
      console.error('Failed to load assignments for semester:', error);
    }
  };

  const handleSemesterChange = (semesterName) => {
    setSelectedSemester(semesterName);
    loadCompletedWorkForSemester(semesterName);
  };


  const childGoals = selectedChild ? goals.filter(g => g.child_id === selectedChild.id) : [];
  const childWork = selectedChild ? completedWork.filter(w => w.child_id === selectedChild.id) : [];
  const childPlans = selectedChild ? lessonPlans.filter(p => p.child_id === selectedChild.id) : [];
  const childPending = selectedChild ? pendingWork.filter(w => w.child_id === selectedChild.id) : [];
  
  // Filter visible lesson plans only (exclude hidden ones)
  const visibleChildPlans = childPlans.filter(p => p.visible_to_children !== false);

  // Get unique subjects for the selected child
  const uniqueSubjectIds = [...new Set([
    ...childWork.map(w => w.subject_id),
    ...childPlans.map(p => p.subject_id)
  ])];
  
  const childSubjects = uniqueSubjectIds
    .map(subjectId => 
      subjects.find(s => 
        String(s.id) === String(subjectId) || 
        (s.replaces_default_id && String(s.replaces_default_id) === String(subjectId))
      )
    )
    .filter(Boolean)
    .filter((subject, index, self) => 
      // Remove duplicates by subject ID
      index === self.findIndex(s => s.id === subject.id)
    );

  // Calculate average grade from completed work (already filtered by semester)
  const avgGrade = childWork.length > 0
    ? Math.round(childWork.reduce((sum, w) => sum + (w.percentage || 0), 0) / childWork.length)
    : 0;


  // Calculate work completed this week (Monday-Friday of current week)
  const today = new Date();
  const currentDayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const daysUntilMonday = currentDayOfWeek === 0 ? -6 : 1 - currentDayOfWeek;
  const monday = new Date(today);
  monday.setDate(today.getDate() + daysUntilMonday);
  monday.setHours(0, 0, 0, 0);
  
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);
  friday.setHours(23, 59, 59, 999);

  const workCompletedThisWeek = childWork.filter(w => {
    const completedDate = new Date(w.completed_date || w.submitted_date);
    return completedDate >= monday && completedDate <= friday;
  });

  const getGradeColor = (grade) => {
    if (!grade) return 'bg-slate-200 text-slate-700';
    const letter = grade.charAt(0);
    if (letter === 'A') return 'bg-green-100 text-green-700 border-green-300';
    if (letter === 'B') return 'bg-blue-100 text-blue-700 border-blue-300';
    if (letter === 'C') return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    if (letter === 'D') return 'bg-orange-100 text-orange-700 border-orange-300';
    return 'bg-red-100 text-red-700 border-red-300';
  };

  const handleAssignmentClick = (assignment) => {
    setSelectedAssignment(assignment);
    setShowAssignmentDialog(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 flex items-center justify-center">
        <div className="text-xl text-slate-600">Loading...</div>
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-8">Dashboard</h1>
          <Card className="p-12 text-center border-0 shadow-lg">
            <Users className="w-24 h-24 text-slate-300 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-slate-900 mb-4">No Children Added Yet</h2>
            <p className="text-slate-600 mb-6">Get started by adding your first child to begin tracking their homeschool progress.</p>
            <Button
              onClick={() => navigate('/settings')}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Your First Child
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Trial Banner */}
        <TrialBanner />
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Dashboard</h1>
          <p className="text-slate-600 text-lg">Welcome back! Here's what's happening with your students.</p>
        </div>

        {/* Student Cards Row with Button and Filter on Right */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,auto] gap-8 mb-8">
          {/* Left: Student Selection Cards */}
          <div className="flex gap-4 overflow-x-auto pb-2">
            {children.map(child => (
              <div key={child.id} className="relative group">
                <button
                  onClick={() => setSelectedChild(child)}
                  className={`flex items-center gap-3 px-6 py-4 rounded-xl transition-all duration-300 transform hover:scale-105 ${
                    selectedChild && selectedChild.id === child.id
                      ? 'bg-gradient-to-br from-blue-500 to-teal-500 text-white shadow-lg'
                      : 'bg-white text-slate-700 hover:shadow-md border border-slate-200'
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold ${
                      selectedChild && selectedChild.id === child.id ? 'bg-white text-blue-600' : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {child.name.charAt(0)}
                  </div>
                  <div className="text-left">
                    <p className="font-semibold">{child.name}</p>
                    <p className={`text-sm ${
                      selectedChild && selectedChild.id === child.id ? 'text-blue-100' : 'text-slate-500'
                    }`}>{child.grade}</p>
                  </div>
                </button>
              </div>
            ))}
          </div>

          {/* Right: Review Pending Work Button and Semester Filter */}
          <div className="flex flex-col justify-start gap-4">
            {/* Review Pending Work Button */}
            <Button
              onClick={() => navigate('/pending')}
              className="relative bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white px-6 py-4 shadow-lg hover:shadow-xl transition-all"
            >
              <Clock className="w-5 h-5 mr-2" />
              Review Pending Work
              {pendingWork.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center animate-pulse">
                  {pendingWork.length}
                </span>
              )}
            </Button>

            {/* Semester Filter */}
            {userSemesters.length > 0 && (
              <div>
                <Label className="text-slate-700 mb-2 block">Filter by Semester:</Label>
                <Select value={selectedSemester} onValueChange={handleSemesterChange}>
                  <SelectTrigger className="w-full md:w-64">
                    <SelectValue placeholder="Select semester" />
                  </SelectTrigger>
                  <SelectContent>
                    {userSemesters.map(semester => (
                      <SelectItem key={semester.name} value={semester.name}>
                        {semester.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* Subjects Section */}
        {childSubjects.length > 0 && (
          <Card className="p-6 border-0 shadow-lg bg-white mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Subjects</h2>
            <div className="flex flex-wrap gap-2">
              {childSubjects.map(subject => (
                <div
                  key={subject.id}
                  className="p-3 rounded-lg cursor-pointer hover:shadow-md transition-all transform hover:scale-105 flex flex-col items-center justify-center min-w-[90px]"
                  style={{ backgroundColor: `${subject.color}20` }}
                  onClick={() => navigate(`/subject-assignments?subject=${subject.id}&child=${selectedChild.id}`)}
                >
                  <div className="text-2xl mb-1">{subject.icon}</div>
                  <p className="font-semibold text-slate-900 text-xs text-center">{subject.name}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Active Goals - Full Width with 2 Columns */}
        <Card className="p-6 border-0 shadow-lg bg-white mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Target className="w-6 h-6 text-teal-600" />
              Active Goals
            </h2>
            <Button
              size="sm"
              className="bg-teal-600 hover:bg-teal-700 text-white"
              onClick={() => navigate('/goals')}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Goal
            </Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {childGoals.map(goal => (
              <div 
                key={goal.id} 
                className="p-4 bg-gradient-to-r from-slate-50 to-teal-50 rounded-lg border border-slate-200 hover:shadow-md hover:border-teal-300 transition-all cursor-pointer"
                onClick={() => navigate('/goals')}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-slate-900">{goal.title}</h3>
                    <p className="text-sm text-slate-600 mt-1">{goal.description}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Progress</span>
                    <span className="font-semibold text-teal-700">{goal.progress}%</span>
                  </div>
                  <Progress value={goal.progress} className="h-2" />
                </div>
                <p className="text-xs text-slate-500 mt-2">Target: {new Date(goal.target_date).toLocaleDateString()}</p>
              </div>
            ))}
            {childGoals.length === 0 && (
              <p className="text-slate-500 text-center py-8 col-span-2">No active goals. Create one to get started!</p>
            )}
          </div>
        </Card>

        {/* Student Assignment Overview - Shows when a child is selected */}
        {selectedChild && (
          <Card className="p-6 border-0 shadow-lg bg-white">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">{selectedChild.name}'s Assignments</h2>
            
            {/* Due Today */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-orange-600 mb-3 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Due Today
              </h3>
              {(() => {
                const dueToday = completedWork.filter(a => {
                  if (!a || !a.child_id || !a.due_date) return false;
                  
                  const dueDate = new Date(a.due_date);
                  const today = new Date();
                  dueDate.setHours(0, 0, 0, 0);
                  today.setHours(0, 0, 0, 0);
                  
                  return a.child_id === selectedChild.id && 
                         dueDate.getTime() === today.getTime() && 
                         a.status === 'assigned'; // Only show assigned (not graded or submitted)
                });
                return dueToday.length === 0 ? (
                  <p className="text-slate-500 text-sm">No assignments due today! 🎉</p>
                ) : (
                  <div className="space-y-3">
                    {dueToday.map(assignment => (
                      <div key={assignment.id} className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <p className="font-semibold text-slate-900">{assignment.title}</p>
                            {assignment.lesson_plan_title && (
                              <p className="text-xs text-slate-500 mt-1">📚 {assignment.lesson_plan_title}</p>
                            )}
                          </div>
                          <Badge className="bg-orange-100 text-orange-700 ml-2">{assignment.assignment_type}</Badge>
                        </div>
                        {assignment.description && (
                          <div className="text-sm text-slate-600 mb-2 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: assignment.description }} />
                        )}
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>📊 {assignment.points_possible} points</span>
                          <span>Due: Today</span>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Overdue */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-red-600 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Overdue
              </h3>
              {(() => {
                const overdue = completedWork.filter(a => {
                  if (!a || !a.child_id || !a.due_date) return false;
                  
                  const dueDate = new Date(a.due_date);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  dueDate.setHours(0, 0, 0, 0);
                  
                  return a.child_id === selectedChild.id && 
                         dueDate < today && 
                         a.status === 'assigned'; // Only show assigned (not graded or submitted)
                });
                return overdue.length === 0 ? (
                  <p className="text-slate-500 text-sm">No overdue assignments! Great work! ✅</p>
                ) : (
                  <div className="space-y-3">
                    {overdue.map(assignment => (
                      <div key={assignment.id} className="p-4 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <p className="font-semibold text-slate-900">{assignment.title}</p>
                            {assignment.lesson_plan_title && (
                              <p className="text-xs text-slate-500 mt-1">📚 {assignment.lesson_plan_title}</p>
                            )}
                          </div>
                          <Badge className="bg-red-100 text-red-700 ml-2">{assignment.assignment_type}</Badge>
                        </div>
                        {assignment.description && (
                          <div className="text-sm text-slate-600 mb-2 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: assignment.description }} />
                        )}
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-500">📊 {assignment.points_possible} points</span>
                          <span className="text-red-600 font-medium">Due: {new Date(assignment.due_date).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Returned for Resubmission */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-yellow-600 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Returned for Resubmission
              </h3>
              {(() => {
                const returned = completedWork.filter(a => 
                  a.child_id === selectedChild.id && (a.needs_revision === true || a.send_back_notes)
                );
                return returned.length === 0 ? (
                  <p className="text-slate-500 text-sm">No assignments returned for resubmission.</p>
                ) : (
                  <div className="space-y-3">
                    {returned.map(assignment => (
                      <div key={assignment.id} className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <p className="font-semibold text-slate-900">{assignment.title}</p>
                            {assignment.lesson_plan_title && (
                              <p className="text-xs text-slate-500 mt-1">📚 {assignment.lesson_plan_title}</p>
                            )}
                          </div>
                          <Badge className="bg-yellow-100 text-yellow-700 ml-2">{assignment.assignment_type}</Badge>
                        </div>
                        {assignment.description && (
                          <div className="text-sm text-slate-600 mb-2 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: assignment.description }} />
                        )}
                        {assignment.send_back_notes && (
                          <div className="bg-yellow-100 p-2 rounded mb-2">
                            <p className="text-xs text-yellow-800"><strong>Teacher Note:</strong> {assignment.send_back_notes}</p>
                          </div>
                        )}
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>📊 {assignment.points_possible} points</span>
                          <span>Due: {new Date(assignment.due_date).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Graded */}
            <div>
              <h3 className="text-lg font-semibold text-green-600 mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Recently Graded
              </h3>
              {(() => {
                const graded = completedWork.filter(a => 
                  a && a.child_id === selectedChild.id && a.status === 'graded'
                ).sort((a, b) => {
                  const dateA = new Date(a.completed_date || a.updated_at || a.due_date);
                  const dateB = new Date(b.completed_date || b.updated_at || b.due_date);
                  return dateB - dateA;
                }).slice(0, 10);
                return graded.length === 0 ? (
                  <p className="text-slate-500 text-sm">No graded assignments yet.</p>
                ) : (
                  <div className="space-y-3">
                    {graded.map(assignment => (
                      <div key={assignment.id} className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <p className="font-semibold text-slate-900">{assignment.title}</p>
                            {assignment.lesson_plan_title && (
                              <p className="text-xs text-slate-500 mt-1">📚 {assignment.lesson_plan_title}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            <Badge className="bg-green-100 text-green-700">{assignment.assignment_type}</Badge>
                            {assignment.grade && (
                              <Badge className="bg-green-600 text-white text-lg px-3">{assignment.grade}</Badge>
                            )}
                          </div>
                        </div>
                        {assignment.description && (
                          <div className="text-sm text-slate-600 mb-2 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: assignment.description }} />
                        )}
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>📊 {assignment.points_awarded || assignment.points_possible}/{assignment.points_possible} points</span>
                          <span>Graded: {new Date(assignment.completed_date || assignment.updated_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </Card>
        )}
      </div>

      {/* Assignment Detail Dialog */}
      <Dialog open={showAssignmentDialog} onOpenChange={setShowAssignmentDialog}>
        <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto">
          {selectedAssignment && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-slate-900">
                  {selectedAssignment.title}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Grade and Date */}
                <div className="flex items-center gap-4 pb-4 border-b">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Grade</p>
                    <Badge className={`${getGradeColor(selectedAssignment.grade)} text-2xl px-6 py-3 border-2`}>
                      {selectedAssignment.grade}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Completed</p>
                    <p className="text-lg font-semibold text-slate-900">
                      {new Date(selectedAssignment.completed_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                {/* Description */}
                {selectedAssignment.description && (
                  <div>
                    <h3 className="font-bold text-slate-900 mb-2">Description</h3>
                    <div className="text-slate-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: selectedAssignment.description }} />
                  </div>
                )}

                {/* Teacher's Notes */}
                {selectedAssignment.notes && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <h3 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                      📝 Teacher's Feedback
                    </h3>
                    <p className="text-blue-900">{selectedAssignment.notes}</p>
                  </div>
                )}

                {/* Attachments/Submitted Work */}
                {selectedAssignment.attachments && selectedAssignment.attachments.length > 0 && (
                  <div>
                    <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                      <Paperclip className="w-5 h-5" />
                      Submitted Work ({selectedAssignment.attachments.length})
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedAssignment.attachments.map((attachment, idx) => (
                        <div
                          key={idx}
                          className="p-4 border-2 border-slate-200 rounded-lg hover:border-blue-400 transition-colors cursor-pointer bg-slate-50"
                        >
                          <div className="flex items-center gap-3 mb-2">
                            {attachment.type === 'image' ? (
                              <ImageIcon className="w-8 h-8 text-blue-600" />
                            ) : (
                              <FileText className="w-8 h-8 text-slate-600" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-slate-900 truncate">{attachment.name}</p>
                              <p className="text-xs text-slate-500 uppercase">{attachment.type}</p>
                            </div>
                          </div>
                          {attachment.type === 'image' && (
                            <div className="mt-2 bg-slate-200 rounded h-32 flex items-center justify-center">
                              <p className="text-slate-500 text-sm">Image Preview</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-3 italic">
                      💡 In a real application, you would be able to view and download these files
                    </p>
                  </div>
                )}

                {(!selectedAssignment.attachments || selectedAssignment.attachments.length === 0) && (
                  <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">No attachments for this assignment</p>
                  </div>
                )}

                {/* Action Button */}
                <div className="pt-4 border-t">
                  <Button
                    onClick={() => navigate('/completed-work')}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    View All Assignments
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Trial Banner Component
const TrialBanner = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [daysRemaining, setDaysRemaining] = useState(null);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const userData = await api.getCurrentUser();
      setUser(userData);
      
      // Calculate days remaining in trial
      if (userData.trial_end) {
        const trialEnd = new Date(userData.trial_end);
        const now = new Date();
        const diff = trialEnd - now;
        // Use Math.floor to show complete days remaining (not round up)
        // This ensures 7-day trial shows max 7 days, not 8
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        setDaysRemaining(days > 0 ? days : 0);
      }
    } catch (error) {
      console.error('Failed to load user data:', error);
    }
  };

  // Don't show if user has active paid subscription
  if (!user || user.subscription_status === 'active') {
    return null;
  }

  // Show different message based on status
  if (user.subscription_status === 'trial' && daysRemaining !== null) {
    return (
      <Card className="mb-6 p-4 bg-gradient-to-r from-purple-500 to-blue-500 text-white border-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6" />
            <div>
              <h3 className="font-semibold text-lg">
                {daysRemaining > 0 
                  ? `${daysRemaining} Day${daysRemaining !== 1 ? 's' : ''} Left in Your Free Trial`
                  : 'Your Trial Has Ended'}
              </h3>
              <p className="text-sm text-purple-100">
                {daysRemaining > 0
                  ? 'Subscribe now to continue accessing all features after your trial ends.'
                  : 'Subscribe to continue using Homeschool Tracker.'}
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/subscription')}
            className="bg-white text-purple-600 hover:bg-purple-50 font-semibold"
          >
            View Plans
          </Button>
        </div>
      </Card>
    );
  }

  // Trial expired
  if (user.subscription_status === 'expired') {
    return (
      <Card className="mb-6 p-4 bg-red-500 text-white border-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6" />
            <div>
              <h3 className="font-semibold text-lg">Trial Expired</h3>
              <p className="text-sm text-red-100">
                Please subscribe to continue using Homeschool Tracker.
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/subscription')}
            className="bg-white text-red-600 hover:bg-red-50 font-semibold"
          >
            Subscribe Now
          </Button>
        </div>
      </Card>
    );
  }

  return null;
};

export default Dashboard;